/**
 * AdminProtectedRoute - wraps the entire admin dashboard.
 * Requires authentication with a team member role.
 */

import { Navigate, useLocation } from 'react-router'
import type { ReactNode } from 'react'
import type { UserRole } from '@provendoro/shared/types'
import { useAdminAuthContext } from './useAdminAuthContext.js'

interface AdminProtectedRouteProps {
  children: ReactNode
  /** If specified, user must have one of these roles */
  allowedRoles?: UserRole[]
}

export function AdminProtectedRoute({
  children,
  allowedRoles,
}: AdminProtectedRouteProps) {
  const { user, loading, hasRole } = useAdminAuthContext()
  const location = useLocation()
  const loginPath = location.pathname.startsWith('/vendor') ? '/vendor/login' : '/login'

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-current border-t-transparent"
          style={{ color: 'var(--color-primary)' }}
        />
      </div>
    )
  }

  if (!user || !user.role) {
    return <Navigate to={loginPath} replace />
  }

  if (allowedRoles && !hasRole(allowedRoles)) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="text-center">
          <h1
            className="mb-2 text-xl font-bold"
            style={{ color: 'var(--color-text)' }}
          >
            Access Denied
          </h1>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            You don&apos;t have permission to access this page.
          </p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
